// @flow
import React, {useMemo, Fragment} from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import PropTypes from 'prop-types';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {Typography, Grid, Hidden, Button, Link} from '@material-ui/core';
import {colors} from '../../../../../assets/styles/colors';
import {useHistory} from 'react-router-dom';
import {generateWhatsappLink} from '../../../../../utils/share';
import {capitalize} from '../../../../../utils/string';

import type {Order, Store} from '../../../../../entities';
import {commaFormat} from '../../../../../utils/number';
import {getCustomDomain} from '../../../../../utils/component';
import {useStoreCurrency} from '../../../../currency/utils';

type Props = {|
  review: Order,
  store: Store,
  tierRestricted?: boolean,
|};

const useStyles = makeStyles(theme => ({
  wrapReview: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 48,
    padding: theme.spacing(0, 1),
    width: '100%',
    color: colors.grey700,
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2, 2),
      alignItems: 'flex-start',
      margin: theme.spacing(1, 0),
      backgroundColor: 'white',
      borderRadius: '15px',
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
      transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    },
    overflow: 'hidden',
  },
  itemColumn: {
    flex: '2 1',
    fontSize: '0.875rem',
  },
  viewOrder: {
    textTransform: 'none',
    fontSize: '14px',
    lineHeight: '24px',
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    color: colors.purple3,
    cursor: 'pointer',
  },
  price: {
    fontSize: '14px',
    lineHeight: '22px',
    display: 'flex',
    alignItems: 'center',
    color: colors.grey800,
  },
  nameCustomer: {
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '24px',
    display: 'flex',
    alignItems: 'center',
    color: colors.grey800,
  },
  header: {
    fontSize: '14px',
    lineHeight: '22px',
    display: 'flex',
    alignItems: 'center',
    color: colors.grey600,
  },
  date: {
    fontSize: '14px',
    lineHeight: '22px',
    display: 'flex',
    alignItems: 'center',
    color: colors.grey700,
  },
  inlineGrid: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  linkWrap: {
    display: 'flex',
  },
  disabledButton: {
    cursor: 'default',
    pointerEvents: 'none',
    color: 'rgba(0, 0, 0, 0.26) !important',
    boxShadow: 'none',
    backgroundColor: 'rgba(0, 0, 0, 0.12) !important',
  },
  button: {
    textDecoration: 'none !important',
    backgroundColor: colors.purple3,
    borderRadius: '32px',
    color: 'white',
    padding: '4px 6px',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    textAlign: 'center',
    display: 'block',
  },
}));

export default function ReviewItem(props: Props) {
  const {review: order, store, tierRestricted} = props;
  const classes = useStyles();
  const currency = useStoreCurrency();

  const history = useHistory();
  const dateString = useMemo(
    () =>
      new Intl.DateTimeFormat('es-PE', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      }).format(order.createdAt),
    [order.createdAt]
  );

  return (
    <Fragment>
      <Hidden smDown>
        <div className={classes.wrapReview}>
          <Typography className={classes.itemColumn}>{order.id}</Typography>
          <Typography className={classes.itemColumn}>{dateString}</Typography>
          <Typography className={`${classes.itemColumn}`}>
            {capitalize(order.customer.firstName)}
          </Typography>
          <Typography className={classes.itemColumn}>
            {commaFormat(order.invoice.detail.total, currency?.symbol)}
          </Typography>

          <Typography
            className={`${classes.itemColumn} ${classes.viewOrder}`}
            variant="button"
            onClick={() => history.push(`../orders/${order.id}`)}
          >
            Ver orden
          </Typography>

          <Typography className={classes.itemColumn}>
            <Link
              className={`${classes.button} ${
                tierRestricted ? classes.disabledButton : ''
              }`}
              target="_blank"
              rel="noopener noreferrer"
              href={generateWhatsappLink(
                order.customer.phone,
                `Buen día! Por favor califica tu compra con nosotros. Accede al siguiente enlace https://${getCustomDomain(
                  store
                )}/order/${order.id}/review?token=${order.token}`
              )}
            >
              Enviar por Whatsapp
            </Link>
          </Typography>
        </div>
      </Hidden>
      <Hidden mdUp>
        <Grid container className={classes.wrapReview}>
          <Grid item xs={12} sm={12} md={12} className={classes.inlineGrid}>
            <Typography
              className={classes.header}
            >{`N° Orden ${order.id}`}</Typography>
            <Typography className={classes.date}>{dateString}</Typography>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Typography className={classes.nameCustomer}>
              {capitalize(order.customer.firstName) +
                ' ' +
                capitalize(order.customer.lastName)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Typography className={classes.price}>
              {commaFormat(order.invoice.detail.total, currency?.symbol)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12} md={12} className={classes.inlineGrid}>
            <div className={classes.linkWrap}>
              <Button
                className={`${classes.itemColumn} ${classes.viewOrder}`}
                onClick={() => history.push(`../orders/${order.id}`)}
              >
                Ver orden
              </Button>
            </div>

            <CustomButton
              variant="contained"
              target="_blank"
              rel="noopener noreferrer"
              href={generateWhatsappLink(
                order.customer.phone,
                `Buen día! Por favor califica tu compra con nosotros. Accede al siguiente enlace https://${getCustomDomain(
                  store
                )}/order/${order.id}/review?token=${order.token}`
              )}
              className={`${classes.button} ${
                tierRestricted ? classes.disabledButton : ''
              }`}
            >
              Enviar por Whatsapp
            </CustomButton>
          </Grid>
        </Grid>
      </Hidden>
    </Fragment>
  );
}

ReviewItem.propTypes = {
  review: PropTypes.object.isRequired,
  store: PropTypes.object.isRequired,
  tierRestricted: PropTypes.bool,
};
